import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, TextNode } from 'lexical';
import * as BrandDefinition from 'modules/BrandDefinition';
import { $isCustomParagraphNode, CustomParagraphNode } from 'modules/Lexical/nodes/CustomParagraphNode';
import { useEffect } from 'react';
import * as Constants from 'const';
import * as Models from 'models';
import { $switchCharacterStyle } from './characterStyle';
import { registerCommands } from './commands';
import {
  $getFontBrandColor,
  $getBrandFont,
  $getFontSize,
  $getCharStyle,
  $updateFontSizeForParagraph,
} from './utils';

export { FONT_COMMAND } from './commands';

export type FontProps = {
  brandColor: Models.BrandColorMap | undefined;
  brandFont: BrandDefinition.BrandFontRef | undefined;
  size: number | undefined;
  charStyle: Constants.CharStyle[];
};

type Props = {
  defaultBrandColor?: BrandDefinition.BrandColorRef;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  onChange: (props: FontProps) => void;
};

export function FontPlugin(props: Props): null {
  const { onChange, colors, fonts, defaultBrandColor } = props;
  const [editor] = useLexicalComposerContext();

  useEffect(
    () => registerCommands(editor, fonts, colors, defaultBrandColor),
    [editor, fonts, colors, defaultBrandColor],
  );

  useEffect(() => {
    return editor.registerMutationListener(
      TextNode,
      (mutatedNodes) => {
        editor.update(() => {
          for (const [nodeKey, mutation] of mutatedNodes) {
            if (mutation === 'destroyed') {
              continue;
            }
            const node = $getNodeByKey<TextNode>(nodeKey);
            if (!node) {
              continue;
            }
            $switchCharacterStyle(node, fonts);
          }
        });
      },
      { skipInitialization: true },
    );
  }, [editor, fonts]);

  useEffect(() => {
    const $readAndNotifyChange = (): void => {
      const brandColor = $getFontBrandColor() ?? defaultBrandColor;
      onChange({
        brandColor: brandColor ? BrandDefinition.findBrandColor(colors, brandColor) : undefined,
        brandFont: $getBrandFont(),
        size: $getFontSize(),
        charStyle: $getCharStyle(),
      });
    };

    editor.getEditorState().read($readAndNotifyChange);

    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read($readAndNotifyChange);
    });
  }, [editor, onChange, colors, defaultBrandColor]);

  useEffect(() => {
    if (!editor.hasNode(CustomParagraphNode)) {
      return;
    }

    return editor.registerMutationListener(
      TextNode,
      (mutatedNodes) => {
        editor.update(() => {
          const uniqueParagraphs = new Set<CustomParagraphNode>();
          for (const [nodeKey] of mutatedNodes) {
            const node = $getNodeByKey<TextNode>(nodeKey);
            if (!node) {
              continue;
            }
            const parent = node.getTopLevelElement();
            if ($isCustomParagraphNode(parent)) {
              uniqueParagraphs.add(parent);
            }
          }
          uniqueParagraphs.forEach($updateFontSizeForParagraph);
        });
      },
    );
  }, [editor]);

  return null;
}
